/**
 * Organisation Management Page
 *
 * Enterprise-grade organisational structure management with F&O integration.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, GitBranch, FileText, BarChart3, AlertTriangle,
  RefreshCw, Search, ChevronLeft, ChevronRight, Clock, CheckCircle, XCircle,
  ArrowUpRight, Briefcase, MapPin, Coins, UserPlus, Edit, MoreHorizontal,
  Archive, Minus, Check, X, FileDown, Printer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { OrgStructureTree } from '@/components/organization/OrgStructureTree';
import { ReportingTree } from '@/components/organization/ReportingTree';
import { PersonDrawer } from '@/components/organization/PersonDrawer';
import { BulkReportingLineDialog } from '@/components/organization/BulkReportingLineDialog';
import { OrganogramExportDialog } from '@/components/organization/OrganogramExportDialog';
import { OrganogramView, type OrganogramViewState } from '@/components/organization/OrganogramView';
import RoleDetailDrawer from '@/pages/role-master/RoleDetailDrawer';
import organizationService, {
  OrgChartResponse, OrgAnalytics, Position, ChangeRequest,
  ChangeRequestStatus, ChangeRequestType, PositionStatus, SyncResult,
} from '@/services/api/organizationService';
import roleMasterService, { RoleMasterDetail } from '@/services/api/roleMasterService';
import { useSyncHistory } from '@/hooks/useSyncHistory';

type TabValue = 'chart' | 'positions' | 'requests' | 'analytics' | 'data-quality';
type ChartView = 'structure' | 'reporting' | 'organogram';

const REQUEST_TYPE_LABELS: Record<ChangeRequestType, string> = {
  new_position: 'New Position',
  position_modification: 'Position Modification',
  position_retirement: 'Position Retirement',
  reporting_line_change: 'Reporting Line Change',
  employee_transfer: 'Team Member Transfer',
  employee_promotion: 'Team Member Promotion',
  department_restructure: 'Department Restructure',
  role_movement: 'Role Movement',
  update_position_dimensions: 'Update Position Dimensions',
};

const REQUEST_STATUS_CONFIG: Record<ChangeRequestStatus, { label: string; color: string; icon: React.ReactNode }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: <FileText className="h-3 w-3" /> },
  pending_approval: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: <Clock className="h-3 w-3" /> },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle className="h-3 w-3" /> },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: <XCircle className="h-3 w-3" /> },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: <XCircle className="h-3 w-3" /> },
  implemented: { label: 'Implemented', color: 'bg-blue-100 text-blue-700', icon: <CheckCircle className="h-3 w-3" /> },
  synced_to_fo: { label: 'Synced', color: 'bg-purple-100 text-purple-700', icon: <ArrowUpRight className="h-3 w-3" /> },
};

const POSITION_STATUS_CONFIG: Record<PositionStatus, { label: string; color: string }> = {
  ACTIVE: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  VACANT: { label: 'Vacant', color: 'bg-amber-100 text-amber-700' },
  FROZEN: { label: 'Frozen', color: 'bg-slate-100 text-slate-700' },
  PENDING_APPROVAL: { label: 'Pending', color: 'bg-blue-100 text-blue-700' },
  RETIRED: { label: 'Retired', color: 'bg-gray-100 text-gray-500' },
};

export const OrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('chart');
  const [chartView, setChartView] = useState<ChartView>('structure');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [orgChartData, setOrgChartData] = useState<OrgChartResponse | null>(null);
  const [analytics, setAnalytics] = useState<OrgAnalytics | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);

  // Structure-tree drawers (one open at a time).
  const [selectedRoleDetail, setSelectedRoleDetail] = useState<RoleMasterDetail | null>(null);
  const [selectedPersonPositionId, setSelectedPersonPositionId] = useState<number | null>(null);

  const { lastSync, refetch: refetchHistory } = useSyncHistory({ limit: 1 });

  // Single source of truth for the tree facet dropdowns: distinct, sorted values
  // lifted from the loaded positions (same idiom as the Positions tab). Passed to
  // BOTH trees as option lists; each tree keeps its own active-filter state.
  const campusOptions = useMemo(() => distinctSorted(positions, (p) => p.campus_code), [positions]);
  const dimCampusOptions = useMemo(() => distinctSorted(positions, (p) => p.dimension_campus), [positions]);
  const dimDeptOptions = useMemo(() => distinctSorted(positions, (p) => p.dimension_department), [positions]);
  const dimFacultyOptions = useMemo(() => distinctSorted(positions, (p) => p.dimension_faculty), [positions]);
  // Two axes for the trees: Worker type = incumbent's employment_category (filled
  // only); Position type = the seat's F&O type normalized, present incl. vacant.
  const workerTypeOptions = useMemo(() => distinctSorted(positions, (p) => p.employment_category), [positions]);
  const positionTypeOptions = useMemo(() => distinctSorted(positions, (p) => p.position_type_group), [positions]);

  const [organogramOpen, setOrganogramOpen] = useState(false);

  // The Positions tab hides RETIRED seats by default, so its badge must count the
  // SAME set the header does (ACTIVE + VACANT). Counting every loaded row made the
  // tab read 4,900 against a header total of 3,014 — the difference was 1,886
  // retired seats nobody had asked to see.
  const livePositionCount = useMemo(
    () => positions.filter((p) => p.status !== 'RETIRED').length,
    [positions],
  );

  // Sync freshness. The nightly F&O job runs 02:00 SAST, so anything older than
  // ~26h means a run was missed — say so, instead of printing a timestamp and
  // leaving it to be noticed by eye.
  const { syncAgeLabel, syncIsStale } = useMemo(() => {
    const iso = lastSync?.sync_started_at;
    if (!iso) return { syncAgeLabel: '', syncIsStale: false };
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    const label = mins < 60 ? `${mins}m ago`
      : mins < 60 * 36 ? `${Math.round(mins / 60)}h ago`
      : `${Math.round(mins / 1440)}d ago`;
    return { syncAgeLabel: label, syncIsStale: mins > 26 * 60 };
  }, [lastSync?.sync_started_at]);
  const [organogramInit, setOrganogramInit] = useState<OrganogramViewState | null>(null);

  // `silent` refreshes the data in place (no full-page loading state) — used for
  // the post-mutation refresh so the trees update without a page flash.
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setLoadError(false);
    try {
      const [chartData, analyticsData, positionsData, requestsData] = await Promise.all([
        organizationService.getOrgChart(),
        organizationService.getOrgAnalytics(),
        organizationService.listPositions({ limit: 20000 }),
        organizationService.listChangeRequests({ limit: 50 }),
      ]);
      // Sort root nodes: roots with children first (main hierarchy), then others
      if (chartData.root_nodes) {
        chartData.root_nodes.sort((a, b) => (b.children?.length || 0) - (a.children?.length || 0));
      }
      setOrgChartData(chartData);
      setAnalytics(analyticsData);
      setPositions(positionsData);
      setChangeRequests(requestsData);
    } catch (err) {
      // No silent mock fallback — masking a real API failure hides outages. Leave
      // the data empty and surface a non-blocking error instead.
      console.error('Failed to load organization data:', err);
      setLoadError(true);
      if (!silent) toast.error('Failed to load organisation data. Please try again.');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  // A mutation landed (reporting-line change / assign, single or bulk): refresh
  // the Reporting tree (orgChartData) + Positions in place, and bump the
  // self-loading Structure tree. Silent so there's no full-page loading flash.
  const [treeReloadKey, setTreeReloadKey] = useState(0);
  const handleMutated = useCallback(() => {
    loadData(true);
    setTreeReloadKey((k) => k + 1);
  }, [loadData]);

  // Sync from F&O and then refresh data
  const handleSyncFromFO = useCallback(async (fullSync = false) => {
    setIsSyncing(true);
    setSyncStatus({ message: fullSync ? 'Full sync from Dynamics F&O (updating all records)...' : 'Syncing from Dynamics F&O...', type: 'info' });
    
    try {
      const results = await organizationService.syncFromFO({ fullSync });
      
      const totalRecords = results.employees.successful_records + 
                          results.positions.successful_records + 
                          results.organizations.successful_records;
      
      const hasErrors = results.employees.errors.length > 0 || 
                       results.positions.errors.length > 0 || 
                       results.organizations.errors.length > 0;
      
      if (hasErrors) {
        const allErrors = [
          ...results.employees.errors,
          ...results.positions.errors,
          ...results.organizations.errors,
        ];
        console.warn('Sync completed with errors:', allErrors);
        setSyncStatus({ 
          message: `Synced ${totalRecords} records with some warnings`, 
          type: 'info' 
        });
      } else {
        setSyncStatus({ 
          message: `Successfully synced ${totalRecords} records from F&O`, 
          type: 'success' 
        });
      }
      
      // Reload data and sync history after sync (+ refresh the Structure tree).
      await Promise.all([loadData(), refetchHistory()]);
      setTreeReloadKey((k) => k + 1);

      // Clear status after 5 seconds
      setTimeout(() => setSyncStatus(null), 5000);

    } catch (err) {
      console.error('F&O sync failed:', err);
      setSyncStatus({
        message: 'Failed to sync from F&O. Please try again.',
        type: 'error'
      });
      await refetchHistory();
      setTimeout(() => setSyncStatus(null), 5000);
    } finally {
      setIsSyncing(false);
    }
  }, [loadData, refetchHistory]);

  useEffect(() => { loadData(); }, [loadData]);

  // Open the ROLE drawer (reused RoleDetailDrawer) — fetch the full detail on demand.
  const handleOpenRole = useCallback(async (entryId: number, _roleCode: string) => {
    setSelectedPersonPositionId(null); // one drawer at a time
    try {
      const detail = await roleMasterService.get(entryId);
      setSelectedRoleDetail(detail);
    } catch (err) {
      console.error('Failed to load role detail', entryId, err);
      toast.error('Failed to load role details');
    }
  }, []);

  // Open the PERSON drawer. userId is unused here (the drawer fetches by
  // positionId); it stays in the signature to match the tree's callback.
  const handleOpenPerson = useCallback((_userId: number | null, positionId: number) => {
    setSelectedRoleDetail(null); // one drawer at a time
    setSelectedPersonPositionId(positionId);
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-gradient-to-r from-heritage-blue to-heritage-blue/90 text-white px-6 py-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <Building2 className="h-5 w-5 shrink-0" />
            <h1 className="truncate text-lg font-semibold">Organisation Management</h1>
          </div>
          {analytics && (
            <div className="flex items-center gap-4">
              <HeaderStat label="positions" value={analytics.total_positions} />
              <HeaderStat label="filled" value={analytics.filled_positions} tone="text-emerald-200" />
              <HeaderStat label="vacant" value={analytics.vacant_positions} tone="text-amber-200" />
              {analytics.pending_requests > 0 && (
                <HeaderStat label="pending" value={analytics.pending_requests} tone="text-blue-200" />
              )}
              {analytics.critical_vacancies > 0 && (
                <HeaderStat label="critical" value={analytics.critical_vacancies} tone="text-red-200" />
              )}
            </div>
          )}
          {lastSync?.sync_started_at && (
            <span
              className={cn(
                'flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs',
                syncIsStale ? 'bg-amber-400/25 text-amber-100' : 'bg-white/10 text-white/70',
              )}
              title={`Last synced ${new Date(lastSync.sync_started_at).toLocaleString()}`}
            >
              {syncIsStale && <AlertTriangle className="h-3 w-3" />}
              Synced {syncAgeLabel}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-white/10 border-white/20 text-white hover:bg-white/20"
              onClick={() => setOrganogramOpen(true)}
              disabled={isLoading}
            >
              <FileDown className="h-4 w-4 mr-2" /> Export organogram
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 bg-white/10 border-white/20 text-white hover:bg-white/20" disabled={isSyncing || isLoading}>
                  <RefreshCw className={cn('h-4 w-4 mr-2', isSyncing && 'animate-spin')} />
                  {isSyncing ? 'Syncing...' : 'Sync from F&O'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleSyncFromFO(false)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Quick Sync (new/changed only)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSyncFromFO(true)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Full Sync (refresh all positions)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Position provisioning lives in the Role Master workspace — send the
                user there rather than a dead button. */}
            <Button
              size="sm"
              className="h-8 bg-white text-heritage-blue hover:bg-white/90"
              onClick={() => navigate('/role-master')}
            >
              <Building2 className="h-4 w-4 mr-1.5" />Role Master
            </Button>
          </div>
        </div>
        {syncStatus && (
          <div className={cn(
            'mt-2 px-3 py-1.5 rounded-md flex items-center gap-2 text-xs',
            syncStatus.type === 'success' && 'bg-emerald-500/20 text-emerald-100',
            syncStatus.type === 'error' && 'bg-red-500/20 text-red-100',
            syncStatus.type === 'info' && 'bg-blue-500/20 text-blue-100',
          )}>
            {syncStatus.type === 'success' && <CheckCircle className="h-4 w-4" />}
            {syncStatus.type === 'error' && <XCircle className="h-4 w-4" />}
            {syncStatus.type === 'info' && <RefreshCw className="h-4 w-4 animate-spin" />}
            {syncStatus.message}
          </div>
        )}
      </div>

      <div className="flex-1 p-6 overflow-hidden">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="h-full flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <TabsList className="bg-gray-100">
              <TabsTrigger value="chart" className="flex items-center gap-2"><GitBranch className="h-4 w-4" />Org Chart</TabsTrigger>
              <TabsTrigger value="positions" className="flex items-center gap-2"><Briefcase className="h-4 w-4" />Positions<Badge variant="secondary" className="ml-1 text-xs">{livePositionCount.toLocaleString()}</Badge></TabsTrigger>
              <TabsTrigger value="requests" className="flex items-center gap-2"><FileText className="h-4 w-4" />Change Requests</TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2"><BarChart3 className="h-4 w-4" />Analytics</TabsTrigger>
            </TabsList>
            {activeTab === 'chart' && (
              <div className="flex items-center bg-white border rounded-lg overflow-hidden">
                <button
                  onClick={() => setChartView('structure')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors',
                    chartView === 'structure' ? 'bg-heritage-blue text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                  )}
                >
                  <Building2 className="h-4 w-4" />Structure
                </button>
                <button
                  onClick={() => setChartView('reporting')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors',
                    chartView === 'reporting' ? 'bg-heritage-blue text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                  )}
                >
                  <GitBranch className="h-4 w-4" />Reporting
                </button>
                <button
                  onClick={() => setChartView('organogram')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors',
                    chartView === 'organogram' ? 'bg-heritage-blue text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                  )}
                >
                  <Printer className="h-4 w-4" />Organogram
                </button>
              </div>
            )}
          </div>

          <TabsContent value="chart" className="flex-1 min-h-0 mt-0">
            {chartView === 'organogram' ? (
              <OrganogramView
                onOpenPerson={handleOpenPerson}
                onOpenRole={handleOpenRole}
                onExportPdf={(state) => { setOrganogramInit(state); setOrganogramOpen(true); }}
                campusOptions={campusOptions}
                dimCampusOptions={dimCampusOptions}
                dimDeptOptions={dimDeptOptions}
                dimFacultyOptions={dimFacultyOptions}
                workerTypeOptions={workerTypeOptions}
                positionTypeOptions={positionTypeOptions}
                reloadKey={treeReloadKey}
              />
            ) : chartView === 'structure' ? (
              <OrgStructureTree
                onOpenRole={handleOpenRole}
                onOpenPerson={handleOpenPerson}
                campusOptions={campusOptions}
                dimCampusOptions={dimCampusOptions}
                dimDeptOptions={dimDeptOptions}
                dimFacultyOptions={dimFacultyOptions}
                workerTypeOptions={workerTypeOptions}
                positionTypeOptions={positionTypeOptions}
                positions={positions}
                reloadKey={treeReloadKey}
              />
            ) : loadError && !orgChartData ? (
              <div className="flex h-full flex-col items-center justify-center rounded-lg border border-border bg-card">
                <AlertTriangle className="mb-3 h-8 w-8 text-destructive" />
                <p className="mb-3 text-sm text-foreground">Failed to load the reporting hierarchy.</p>
                <Button variant="outline" size="sm" onClick={() => loadData()}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Retry
                </Button>
              </div>
            ) : (
              <ReportingTree
                data={orgChartData}
                onOpenPerson={handleOpenPerson}
                campusOptions={campusOptions}
                dimCampusOptions={dimCampusOptions}
                dimDeptOptions={dimDeptOptions}
                dimFacultyOptions={dimFacultyOptions}
                workerTypeOptions={workerTypeOptions}
                positionTypeOptions={positionTypeOptions}
              />
            )}
          </TabsContent>
          <TabsContent value="positions" className="flex-1 mt-0 overflow-auto">
            <PositionsTable
              positions={positions}
              onOpen={(pos) => handleOpenPerson(pos.incumbent_user_id ?? null, pos.id)}
              onReload={handleMutated}
            />
          </TabsContent>
          <TabsContent value="requests" className="flex-1 mt-0 overflow-auto">
            <ChangeRequestsTable requests={changeRequests} />
          </TabsContent>
          <TabsContent value="analytics" className="flex-1 mt-0 overflow-auto">
            {analytics && <AnalyticsDashboard analytics={analytics} />}
          </TabsContent>
        </Tabs>
      </div>

      {/* Structure-tree drawers */}
      <RoleDetailDrawer
        open={selectedRoleDetail !== null}
        onOpenChange={(o) => { if (!o) setSelectedRoleDetail(null); }}
        entry={selectedRoleDetail}
        onEdit={() => toast('Edit this role in the Role Master workspace.')}
        onDeprecate={() => { /* deprecate is driven from the Role Master workspace */ }}
      />
      <PersonDrawer
        open={selectedPersonPositionId !== null}
        onOpenChange={(o) => { if (!o) setSelectedPersonPositionId(null); }}
        positionId={selectedPersonPositionId}
        onOpenPerson={handleOpenPerson}
        onChanged={handleMutated}
      />
      <OrganogramExportDialog
        open={organogramOpen}
        onOpenChange={(o) => { setOrganogramOpen(o); if (!o) setOrganogramInit(null); }}
        initial={organogramInit}
        campusOptions={campusOptions}
        dimCampusOptions={dimCampusOptions}
        dimDeptOptions={dimDeptOptions}
        dimFacultyOptions={dimFacultyOptions}
        workerTypeOptions={workerTypeOptions}
        positionTypeOptions={positionTypeOptions}
      />
    </div>
  );
};

/** One inline metric in the command bar (replaces the old 5-card stat grid). */
const HeaderStat: React.FC<{ label: string; value: number; tone?: string }> = ({ label, value, tone }) => (
  <span className="flex items-baseline gap-1 whitespace-nowrap">
    <span className={cn('text-base font-semibold tabular-nums', tone ?? 'text-white')}>
      {value.toLocaleString()}
    </span>
    <span className="text-xs text-white/70">{label}</span>
  </span>
);

const POSITIONS_PAGE_SIZE = 100;

// Distinct, non-empty, locale-sorted values of one field across positions — powers
// every self-populating filter dropdown so options mirror the real data.
function distinctSorted(positions: Position[], get: (p: Position) => string | null | undefined): string[] {
  const set = new Set<string>();
  for (const p of positions) {
    const v = get(p);
    if (v) set.add(v);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// Max seats per bulk reporting-line change — must match the backend
// `_MAX_BULK_CODES` guard so select-all on a large filter can't dead-end on a 400.
const BULK_MAX = 200;

/**
 * Tri-state header checkbox. The shared Checkbox primitive can't render an
 * indeterminate state (its visual only shows a tick when `checked`), so the
 * select-all header uses this small control: tick = all, minus = partial.
 */
const TriStateCheckbox: React.FC<{
  checked: boolean;
  partial: boolean;
  disabled?: boolean;
  onToggle: () => void;
  label: string;
}> = ({ checked, partial, disabled, onToggle, label }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={partial ? 'mixed' : checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    className={cn(
      'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-heritage-blue ring-offset-background',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue focus-visible:ring-offset-2',
      (checked || partial) && 'bg-heritage-blue text-white',
      disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
    )}
  >
    {partial ? <Minus className="h-3 w-3" /> : checked ? <Check className="h-3 w-3" /> : null}
  </button>
);

/**
 * RM-grouped, vacancy-aware positions table.
 *
 * Owns its own toolbar (search + campus + function + vacant-only + include-retired),
 * a filtered summary line, and simple client-side pagination — there are ~4,700
 * positions, so we never render the full filtered set at once. The whole ROW is the
 * target: clicking (or Enter/Space on) any row opens the PersonDrawer for that seat
 * via `onOpen` (a vacant seat still opens its drawer).
 *
 * Bulk reporting-line: a leading checkbox column drives a Set of selected
 * `position_code`s. Select-all covers the ENTIRE filtered set (all pages), not just
 * the visible page. RETIRED seats are excluded by default (an "Include retired"
 * toggle reveals them) and are never bulk-target-eligible. When ≥1 eligible seat is
 * selected, a sticky action bar opens {@link BulkReportingLineDialog}.
 */
const PositionsTable: React.FC<{
  positions: Position[];
  onOpen: (position: Position) => void;
  onReload: () => void;
}> = ({ positions, onOpen, onReload }) => {
  const [search, setSearch] = useState('');
  const [campus, setCampus] = useState<string>('all');
  const [func, setFunc] = useState<string>('all');
  const [dimCampus, setDimCampus] = useState<string>('all');
  const [dimDept, setDimDept] = useState<string>('all');
  const [dimFaculty, setDimFaculty] = useState<string>('all');
  const [workerType, setWorkerType] = useState<Set<string>>(() => new Set());
  const [positionType, setPositionType] = useState<Set<string>>(() => new Set());
  const [vacantOnly, setVacantOnly] = useState(false);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [page, setPage] = useState(1);

  // Bulk-selection state: selected seats tracked by position_code so the
  // selection survives pagination (we only ever render one page at a time).
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  // Distinct, sorted option lists self-populated from the loaded positions, so a
  // filter only ever offers values that actually exist in the data.
  const campusOptions = useMemo(() => distinctSorted(positions, (p) => p.campus_code), [positions]);
  const functionOptions = useMemo(() => distinctSorted(positions, (p) => p.functional_area), [positions]);
  const dimCampusOptions = useMemo(() => distinctSorted(positions, (p) => p.dimension_campus), [positions]);
  const dimDeptOptions = useMemo(() => distinctSorted(positions, (p) => p.dimension_department), [positions]);
  const dimFacultyOptions = useMemo(() => distinctSorted(positions, (p) => p.dimension_faculty), [positions]);
  // Two independent axes. Worker type = the incumbent's employment_category
  // (filled seats only — a vacancy has no worker). Position type = the seat's own
  // F&O type normalized (Perm/FTC/ICA/Casual), present on every seat incl. vacant.
  const workerTypeOptions = useMemo(() => distinctSorted(positions, (p) => p.employment_category), [positions]);
  const positionTypeOptions = useMemo(() => distinctSorted(positions, (p) => p.position_type_group), [positions]);

  // id -> Position map (built once per positions load) so a row can resolve its
  // `reports_to_position_id` to the parent seat's incumbent for the Reports-to column.
  const positionsById = useMemo(() => {
    const m = new Map<number, Position>();
    for (const p of positions) m.set(p.id, p);
    return m;
  }, [positions]);

  // Current reporting parent, resolved through the loaded set.
  const reportsToLabel = useCallback((p: Position): string => {
    if (!p.reports_to_position_id) return '—';
    const parent = positionsById.get(p.reports_to_position_id);
    if (!parent) return '—';
    if (parent.is_vacant) return 'Vacant';
    return parent.incumbent_name || 'Vacant';
  }, [positionsById]);

  // Compose every filter (AND). RETIRED seats are excluded unless the toggle is on.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return positions.filter((p) => {
      if (!includeRetired && p.status === 'RETIRED') return false;
      if (campus !== 'all' && p.campus_code !== campus) return false;
      if (func !== 'all' && p.functional_area !== func) return false;
      if (dimCampus !== 'all' && p.dimension_campus !== dimCampus) return false;
      if (dimDept !== 'all' && p.dimension_department !== dimDept) return false;
      if (dimFaculty !== 'all' && p.dimension_faculty !== dimFaculty) return false;
      if (workerType.size > 0 && !workerType.has(p.employment_category ?? '')) return false;
      if (positionType.size > 0 && !positionType.has(p.position_type_group ?? '')) return false;
      if (vacantOnly && !p.is_vacant) return false;
      if (q) {
        const hay = `${p.title} ${p.position_code} ${p.incumbent_name ?? ''} ${p.role_code ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [positions, search, campus, func, dimCampus, dimDept, dimFaculty, workerType, positionType, vacantOnly, includeRetired]);

  // Bulk-eligible = filtered AND not RETIRED. A RETIRED seat can never be a
  // reporting-line target, so it's never selectable even when shown.
  const eligibleCodes = useMemo(() => {
    const s = new Set<string>();
    for (const p of filtered) if (p.status !== 'RETIRED') s.add(p.position_code);
    return s;
  }, [filtered]);

  // When the filter set changes, drop any selection that's no longer visible
  // (intersect with the eligible set) — the least-surprising behaviour: keep the
  // seats you can still see, forget the ones you filtered away.
  useEffect(() => {
    setSelected((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const c of prev) {
        if (eligibleCodes.has(c)) next.add(c);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [eligibleCodes]);

  // Reset to the first page whenever a filter/search changes.
  useEffect(() => { setPage(1); }, [search, campus, func, dimCampus, dimDept, dimFaculty, workerType, positionType, vacantOnly, includeRetired]);

  const total = filtered.length;
  const vacant = useMemo(() => filtered.reduce((n, p) => n + (p.is_vacant ? 1 : 0), 0), [filtered]);
  const filled = total - vacant;

  // Count only eligible-and-selected — robust to the transient render between a
  // filter change and the prune effect settling.
  const selectedEligibleCount = useMemo(() => {
    let n = 0;
    for (const c of eligibleCodes) if (selected.has(c)) n++;
    return n;
  }, [eligibleCodes, selected]);
  const selectedCodes = useMemo(
    () => Array.from(selected).filter((c) => eligibleCodes.has(c)),
    [selected, eligibleCodes],
  );
  const allChecked = eligibleCodes.size > 0 && selectedEligibleCount === eligibleCodes.size;
  const partialChecked = selectedEligibleCount > 0 && !allChecked;

  const toggleOne = useCallback((code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(() => (allChecked ? new Set<string>() : new Set(eligibleCodes)));
  }, [allChecked, eligibleCodes]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const toggleWorkerType = useCallback((e: string) => {
    setWorkerType((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  }, []);
  const togglePositionType = useCallback((e: string) => {
    setPositionType((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  }, []);

  // Job complete: forget the selection and pull fresh positions so the new
  // reporting lines (and the Reports-to column) reflect the change.
  const handleBulkCompleted = useCallback(() => {
    setSelected(new Set());
    onReload();
  }, [onReload]);

  const pageCount = Math.max(1, Math.ceil(total / POSITIONS_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * POSITIONS_PAGE_SIZE;
  const pageItems = filtered.slice(start, start + POSITIONS_PAGE_SIZE);
  const rangeStart = total === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(start + POSITIONS_PAGE_SIZE, total);

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search title, code, incumbent, role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-72 pl-9"
          />
        </div>
        <Select value={campus} onValueChange={setCampus}>
          <SelectTrigger className="h-9 w-[150px]"><MapPin className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campuses</SelectItem>
            {campusOptions.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={func} onValueChange={setFunc}>
          <SelectTrigger className="h-9 w-[210px]"><Building2 className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="All functions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All functions</SelectItem>
            {functionOptions.map((f) => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* F&O financial-dimension filters (budget codes) — a distinct group from
            the incumbent's physical campus above. */}
        <Select value={dimCampus} onValueChange={setDimCampus}>
          <SelectTrigger className="h-9 w-[160px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Fin. campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. campuses</SelectItem>
            {dimCampusOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimDept} onValueChange={setDimDept}>
          <SelectTrigger className="h-9 w-[190px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Fin. department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. departments</SelectItem>
            {dimDeptOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimFaculty} onValueChange={setDimFaculty}>
          <SelectTrigger className="h-9 w-[180px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Faculty" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All faculties</SelectItem>
            {dimFacultyOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Worker type = the incumbent's employment_category (filled seats only —
            a vacancy has no worker; incl. Intern/Learner which are worker-only). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', workerType.size > 0 && 'border-heritage-blue')}>
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> Worker type
              {workerType.size > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{workerType.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filter by worker type (filled seats)</DropdownMenuLabel>
            {workerTypeOptions.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No worker types</div>
            )}
            {workerTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem
                key={e}
                checked={workerType.has(e)}
                onCheckedChange={() => toggleWorkerType(e)}
                onSelect={(ev) => ev.preventDefault()}
              >
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
            {workerType.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={() => setWorkerType(new Set())}
                  className="w-full px-2 py-1.5 text-left text-xs text-destructive hover:bg-muted"
                >
                  Clear worker type
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Position type = the seat's own F&O type normalized (Perm/FTC/ICA/Casual).
            On EVERY seat incl. vacant → use this to keep gaps in scope. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', positionType.size > 0 && 'border-heritage-blue')}>
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> Position type
              {positionType.size > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{positionType.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filter by position type (incl. vacant)</DropdownMenuLabel>
            {positionTypeOptions.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No position types</div>
            )}
            {positionTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem
                key={e}
                checked={positionType.has(e)}
                onCheckedChange={() => togglePositionType(e)}
                onSelect={(ev) => ev.preventDefault()}
              >
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
            {positionType.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={() => setPositionType(new Set())}
                  className="w-full px-2 py-1.5 text-left text-xs text-destructive hover:bg-muted"
                >
                  Clear position type
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant={vacantOnly ? 'default' : 'outline'}
          size="sm"
          className={cn('h-9', vacantOnly && 'bg-amber-500 text-white hover:bg-amber-500/90')}
          onClick={() => setVacantOnly((v) => !v)}
        >
          <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Vacant only
        </Button>
        <Button
          variant={includeRetired ? 'default' : 'outline'}
          size="sm"
          className={cn('h-9', includeRetired && 'bg-slate-600 text-white hover:bg-slate-600/90')}
          onClick={() => setIncludeRetired((v) => !v)}
        >
          <Archive className="mr-1.5 h-3.5 w-3.5" /> Include retired
        </Button>
      </div>

      {/* Filtered summary */}
      <p className="text-sm text-muted-foreground">
        {Math.round(total).toLocaleString()} positions ·{' '}
        <span className="text-emerald-600 dark:text-emerald-400">{Math.round(filled).toLocaleString()} filled</span> ·{' '}
        <span className="text-amber-600 dark:text-amber-400">{Math.round(vacant).toLocaleString()} vacant</span>
      </p>

      {/* Sticky bulk-action bar — shown once at least one eligible seat is selected.
          The backend caps a bulk at BULK_MAX seats, so gate the action here rather
          than dead-ending on a 400 (e.g. select-all on a large filter). */}
      {selectedEligibleCount > 0 && (
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 rounded-md border border-heritage-blue/30 bg-heritage-blue/5 px-3 py-2 backdrop-blur-sm dark:bg-heritage-blue/10">
          <span className="text-sm font-medium text-foreground">
            {selectedEligibleCount.toLocaleString()} selected
            {selectedEligibleCount > BULK_MAX && (
              <span className="ml-2 font-normal text-amber-600 dark:text-amber-400">
                — up to {BULK_MAX} at a time; narrow the filter
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-8" onClick={clearSelection}>
              <X className="mr-1 h-3.5 w-3.5" /> Clear
            </Button>
            <Button
              size="sm"
              className="h-8"
              disabled={selectedEligibleCount > BULK_MAX}
              onClick={() => setBulkOpen(true)}
            >
              <GitBranch className="mr-1.5 h-3.5 w-3.5" /> Set reporting line ({selectedEligibleCount.toLocaleString()})
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <TriStateCheckbox
                  checked={allChecked}
                  partial={partialChecked}
                  disabled={eligibleCodes.size === 0}
                  onToggle={toggleAll}
                  label="Select all eligible positions"
                />
              </TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Function</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Campus</TableHead>
              <TableHead>Incumbent</TableHead>
              <TableHead>Worker type</TableHead>
              <TableHead>Position type</TableHead>
              <TableHead>Reports to</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="py-10 text-center text-sm text-muted-foreground">
                  No positions match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              pageItems.map((pos) => {
                const isRetired = pos.status === 'RETIRED';
                return (
                  <TableRow
                    key={pos.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpen(pos)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(pos); }
                    }}
                    className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-heritage-blue/50"
                  >
                    {/* Checkbox in its own cell — stop click AND keydown so neither
                        a mouse click nor Space/Enter on the box bubbles to the row
                        (which would open the drawer). RETIRED seats aren't selectable. */}
                    <TableCell
                      className="w-10"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {isRetired ? (
                        <span className="sr-only">Retired — not selectable</span>
                      ) : (
                        <Checkbox
                          checked={selected.has(pos.position_code)}
                          onCheckedChange={() => toggleOne(pos.position_code)}
                          aria-label={`Select ${pos.position_code}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-foreground">{pos.title}</p>
                      <p className="font-mono text-xs text-muted-foreground">{pos.position_code}</p>
                    </TableCell>
                    <TableCell>
                      {pos.role_code
                        ? <span className="font-mono text-xs text-foreground">{pos.role_code}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-foreground">{pos.functional_area || '—'}</TableCell>
                    <TableCell className="text-sm text-foreground">{pos.department || '—'}</TableCell>
                    <TableCell className="text-sm text-foreground">{pos.campus_name || pos.campus_code || '—'}</TableCell>
                    <TableCell>
                      {pos.is_vacant
                        ? <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Vacant</Badge>
                        : <span className="text-sm text-foreground">{pos.incumbent_name || '—'}</span>}
                    </TableCell>
                    {/* Worker type = the incumbent's employment_category (blank for a
                        vacant seat — no worker). */}
                    <TableCell className="text-sm text-muted-foreground">
                      {pos.employment_category || '—'}
                    </TableCell>
                    {/* Position type = the seat's own F&O type, present incl. when vacant. */}
                    <TableCell className="text-sm text-muted-foreground">
                      {pos.position_type_group || '—'}
                    </TableCell>
                    <TableCell className="max-w-[10rem] truncate text-sm text-muted-foreground" title={reportsToLabel(pos)}>
                      {reportsToLabel(pos)}
                    </TableCell>
                    <TableCell>
                      <Badge className={POSITION_STATUS_CONFIG[pos.status].color}>{POSITION_STATUS_CONFIG[pos.status].label}</Badge>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {total.toLocaleString()}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <span className="text-xs">Page {safePage.toLocaleString()} of {pageCount.toLocaleString()}</span>
            <Button variant="outline" size="sm" className="h-8" disabled={safePage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <BulkReportingLineDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        selectedCodes={selectedCodes}
        onCompleted={handleBulkCompleted}
      />
    </div>
  );
};

const ChangeRequestsTable: React.FC<{ requests: ChangeRequest[] }> = ({ requests }) => (
  <Card>
    <Table>
      <TableHeader><TableRow><TableHead>Request</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Requested</TableHead></TableRow></TableHeader>
      <TableBody>
        {requests.map((req) => (
          <TableRow key={req.id}>
            <TableCell><p className="font-medium">{req.title}</p><p className="text-sm text-gray-500">{req.request_number}</p></TableCell>
            <TableCell><Badge variant="outline">{REQUEST_TYPE_LABELS[req.request_type]}</Badge></TableCell>
            <TableCell><Badge className={cn('flex items-center gap-1 w-fit', REQUEST_STATUS_CONFIG[req.status].color)}>{REQUEST_STATUS_CONFIG[req.status].icon}{REQUEST_STATUS_CONFIG[req.status].label}</Badge></TableCell>
            <TableCell className="text-sm text-gray-500">{new Date(req.requested_at).toLocaleDateString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Card>
);

const AnalyticsDashboard: React.FC<{ analytics: OrgAnalytics }> = ({ analytics }) => (
  <div className="grid grid-cols-3 gap-6">
    <Card className="col-span-2">
      <CardHeader><CardTitle>Positions by Function</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Object.entries(analytics.positions_by_function).map(([func, count]) => (
            <div key={func}>
              <div className="flex items-center justify-between mb-1"><span className="text-sm font-medium">{func}</span><span className="text-sm text-gray-500">{count}</span></div>
              <Progress value={(count / analytics.total_positions) * 100} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    <div className="space-y-6">
      <Card><CardHeader><CardTitle>Vacancy Rate</CardTitle></CardHeader><CardContent><div className="text-4xl font-bold text-amber-600">{analytics.vacancy_rate.toFixed(1)}%</div></CardContent></Card>
      <Card><CardHeader><CardTitle>Span of Control</CardTitle></CardHeader><CardContent><div className="text-4xl font-bold text-heritage-blue">{analytics.avg_span_of_control.toFixed(1)}</div></CardContent></Card>
    </div>
  </div>
);

export default OrganizationPage;
